import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Linkedin, Twitter } from "lucide-react";
import Logo from "./Logo";

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={styles.grid}>
          {/* Company Info */}
          <div style={styles.column}>
            <div style={styles.logo}>
              <Logo variant="dark" size="md" />
            </div>
            <p style={styles.description}>{t("footer.tagline")}</p>
            <div style={styles.socialLinks}>
              <a href="#" style={styles.socialIcon}><Linkedin size={20} /></a>
              <a href="#" style={styles.socialIcon}><Twitter size={20} /></a>
            </div>
          </div>

          {/* Quick Links */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>{t("footer.columns.product")}</h3>
            <Link to="/producto" style={styles.link}>{t("footer.links.features")}</Link>
            <Link to="/stablecoin" style={styles.link} data-testid="footer-stablecoin-link">{t("footer.links.stablecoin")}</Link>
            <Link to="/ai-agents" style={styles.link} data-testid="footer-ai-agents-link">{t("footer.links.aiAgents")}</Link>
            <Link to="/casos-de-uso" style={styles.link}>{t("footer.links.useCases")}</Link>
            <Link to="/integracion" style={styles.link}>{t("footer.links.integration")}</Link>
            <Link to="/seguridad" style={styles.link}>{t("footer.links.security")}</Link>
          </div>

          {/* Resources */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>{t("footer.columns.resources")}</h3>
            <a href="https://caas-undostres.readme.io/reference" target="_blank" rel="noopener noreferrer" style={styles.link} data-testid="footer-doc-link">{t("footer.links.apiDocs")}</a>
            <a href="#" style={styles.link}>{t("footer.links.startGuides")}</a>
            <a href="#" style={styles.link}>{t("footer.links.helpCenter")}</a>
            <a href="#" style={styles.link}>{t("footer.links.systemStatus")}</a>
          </div>

          {/* Contact */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>{t("footer.columns.contact")}</h3>
            <div style={styles.contactItem}>
              <Mail size={16} color="#00A8E8" />
              <span style={styles.contactText}>tarjetasdecredito@undostres.com.mx</span>
            </div>
            <div style={styles.contactItem}>
              <Phone size={16} color="#00A8E8" />
              <span style={styles.contactText}>+52 55 1234 5678</span>
            </div>
            <div style={styles.contactItem}>
              <MapPin size={16} color="#00A8E8" />
              <span style={styles.contactText}>{t("footer.contact.location")}</span>
            </div>
          </div>
        </div>

        <div style={styles.bottom}>
          <p style={styles.copyright}>{t("footer.copyright")}</p>
          <div style={styles.legal}>
            <a href="#" style={styles.legalLink}>{t("footer.legal.terms")}</a>
            <span style={styles.separator}>•</span>
            <a href="#" style={styles.legalLink}>{t("footer.legal.privacy")}</a>
            <span style={styles.separator}>•</span>
            <a href="#" style={styles.legalLink}>{t("footer.legal.compliance")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const styles = {
  footer: { background: "rgba(10, 10, 15, 0.95)", borderTop: "1px solid rgba(255, 255, 255, 0.1)", padding: "60px 20px 30px" },
  container: { maxWidth: "1200px", margin: "0 auto" },
  grid: { display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1.5fr", gap: "40px", marginBottom: "40px" },
  column: { display: "flex", flexDirection: "column", gap: "16px" },
  logo: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" },
  logoText: { fontSize: "20px", fontWeight: "700", background: "linear-gradient(135deg, #00A8E8 0%, #4780EA 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" },
  description: { color: "rgba(255, 255, 255, 0.6)", fontSize: "14px", lineHeight: "1.6" },
  socialLinks: { display: "flex", gap: "12px", marginTop: "8px" },
  socialIcon: { width: "36px", height: "36px", borderRadius: "8px", background: "rgba(255, 255, 255, 0.05)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255, 255, 255, 0.7)", transition: "background 0.2s, color 0.2s", textDecoration: "none" },
  columnTitle: { color: "white", fontSize: "16px", fontWeight: "600", marginBottom: "8px" },
  link: { color: "rgba(255, 255, 255, 0.6)", textDecoration: "none", fontSize: "14px", transition: "color 0.2s" },
  contactItem: { display: "flex", alignItems: "center", gap: "10px" },
  contactText: { color: "rgba(255, 255, 255, 0.6)", fontSize: "14px" },
  bottom: { paddingTop: "30px", borderTop: "1px solid rgba(255, 255, 255, 0.1)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "20px" },
  copyright: { color: "rgba(255, 255, 255, 0.5)", fontSize: "14px" },
  legal: { display: "flex", gap: "8px", alignItems: "center" },
  legalLink: { color: "rgba(255, 255, 255, 0.5)", textDecoration: "none", fontSize: "14px", transition: "color 0.2s" },
  separator: { color: "rgba(255, 255, 255, 0.3)", fontSize: "14px" }
};

// Add media query for responsive design
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 768px) {
    footer > div > div:first-child {
      grid-template-columns: 1fr !important;
    }
    footer > div > div:last-child {
      flex-direction: column;
      align-items: flex-start !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default Footer;
